<template>
  <section>
    <div class="flex" :class="!table.id ? 'justify-between' : 'justify-end'">
      <div v-if="!table.id" id="add-table-or-room-heading" class="font-heading font-semibold">
        <template v-if="!table.standing_room">Add Table</template>
        <template v-else>Add Standing Room</template>
      </div>
      <XClose @click="$emit('closeView')" class="cursor-pointer" />
    </div>
    <hr class="mt-6 mb-2 border-linear-subtle" />
    <div class="mb-8">
      <ErrorDisplay v-bind="ui.error" />
      <div class="my-6 flex">
        <div class="flex flex-col space-y-5 w-1/2 pr-4">
          <div class="w-full">
            <TextInput
              id="table-view-name-input"
              v-model="name"
              label="Table Name"
              placeholder="Name"
              class="mb-2"
            />
          </div>
          <div v-if="!table.standing_room" class="w-full">
            <NumberInput
              id="table-view-min-guests"
              v-model="min_max_guests[0]"
              label="Min Guests"
            />
          </div>
          <div class="w-full">
            <label class="font-label text-fg-secondary uppercase text-xs">Shape</label>
            <svg
              class="max-w-[150px] h-10 mr-2"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 150 37"
              preserveAspectRatio="xMidYMid meet"
            >
              <circle
                id="shape-circle"
                cx="18"
                cy="18"
                r="15"
                stroke-width="2"
                class="stroke-gray-30 cursor-pointer"
                :class="selectedTableShape === TableShape.CIRCLE ? 'fill-gray-40' : 'fill-white'"
                @click="setSelectedTableShape(TableShape.CIRCLE)"
              />
              <rect
                id="shape-square"
                x="45"
                y="5"
                width="27"
                height="27"
                rx="2"
                stroke-width="2"
                class="stroke-gray-30 cursor-pointer"
                :class="selectedTableShape === TableShape.SQUARE ? 'fill-gray-40' : 'fill-white'"
                @click="setSelectedTableShape(TableShape.SQUARE)"
              />
              <path
                id="shape-triangle"
                d="M 86 5 L 86 31 L 110 5 Z"
                stroke-width="2"
                class="stroke-gray-30 cursor-pointer"
                stroke-linejoin="round"
                :class="selectedTableShape === TableShape.TRIANGLE ? 'fill-gray-40' : 'fill-white'"
                @click="setSelectedTableShape(TableShape.TRIANGLE)"
              />
              <path
                id="shape-half-circle"
                d="M 124 5 C 145 5, 145 31, 124 31 Z"
                stroke-width="2"
                class="stroke-gray-30 cursor-pointer"
                stroke-linejoin="round"
                :class="
                  selectedTableShape === TableShape.HALF_CIRCLE ? 'fill-gray-40' : 'fill-white'
                "
                @click="setSelectedTableShape(TableShape.HALF_CIRCLE)"
              />
            </svg>
          </div>
          <template v-if="!table.standing_room">
            <div id="table-type-btn">
              <RadioInput
                v-for="tableType in table_types"
                :key="tableType.id"
                radioName="table_types_radio"
                :value="tableType.id.toString()"
                v-model="table_type_id"
                :label="tableType.name"
                :uid="`table-type-btn-${tableType.id}`"
              />
            </div>
            <div id="held-btn">
              <RadioInput
                v-for="option in heldOptions"
                :key="`help-option-${option}`"
                radioName="held_options_radio"
                :value="option"
                v-model="heldOption"
                :label="option"
                :uid="`radio-held-option-${option}`"
              />
            </div>
          </template>
        </div>
        <div class="flex flex-col space-y-5 w-1/2 pl-4">
          <div class="w-full">
            <NumberInput
              id="table-view-price-input"
              v-model="default_price_per_person"
              :label="isDefaultPrice ? 'Default Price Per Person' : 'Price Per Person'"
              currency
              class="mb-2"
            />
          </div>
          <div class="w-full">
            <NumberInput
              id="table-view-max-guests"
              v-model="min_max_guests[1]"
              label="Max Guests"
            />
          </div>
          <NumberInput
            id="table-view-rotation-input"
            v-model="rotation"
            label="Rotation"
            min="0"
            max="360"
            step="15"
            classes="w-1/2"
          />
          <div class="flex items-baseline justify-between">
            <NumberInput
              id="table-view-width-input"
              v-model="width"
              :label="widthLabel"
              :min="table.standing_room ? '1' : '0.5'"
              :max="table.standing_room ? '50' : '10'"
              :step="table.standing_room ? '1' : '0.5'"
              classes="w-1/2 pr-2"
            />
            <div v-if="!usesDiameter" class="relative w-1/2">
              <span
                class="cursor-pointer text-fg-link-primary font-sans text-sm absolute top-0 left-1/2 -translate-y-1"
                id="table-view-width-height-equal-button"
                @click="isWidthAndHeightEqual = !isWidthAndHeightEqual"
              >
                {{ isWidthAndHeightEqual ? 'Equal' : 'Unequal' }}
              </span>
              <NumberInput
                v-model="height"
                label="Height"
                id="table-view-height-input"
                :min="table.standing_room ? '1' : '0.5'"
                :max="table.standing_room ? '50' : '10'"
                :step="table.standing_room ? '1' : '0.5'"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex gap-x-8">
      <ButtonDefault
        v-if="table.id"
        @click="$emit('closeView')"
        label="Discard Edits"
        classes="w-1/2"
        alt
      />
      <ButtonDefault id="table-view-save-btn" @click="onSaveTable" label="Save" classes="w-1/2" />
    </div>
    <hr class="my-6 border-linear-subtle" />
    <div class="flex gap-x-8">
      <ButtonDefault
        v-if="table.id"
        @click="deleteTableDialog = true"
        label="Delete Table"
        classes="w-1/2"
        alt
      />
      <div class="w-1/2" />
    </div>
    <SlotModal :display="deleteTableDialog" key="delete-table" @close="deleteTableDialog = false">
      <template #header>Delete Table</template>
      <template #default>
        <div class="mb-4">Are you sure you want to delete table {{ table.name }}?</div>
        <div class="flex justify-start">
          <ButtonDefault @click="$emit('deleteTable')" label="Yes" class="mr-4" />
          <ButtonDefault @click="deleteTableDialog = false" label="No" alt />
        </div>
      </template>
    </SlotModal>
  </section>
</template>

<script lang="ts">
import { mapState, mapStores } from 'pinia'
import { defineComponent } from 'vue'

import XClose from '@/assets/xClose.svg?component'
import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import ErrorDisplay from '@/common/components/atoms/ErrorDisplay.vue'
import NumberInput from '@/common/components/molecules/NumberInput.vue'
import RadioInput from '@/common/components/molecules/RadioInput.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import SlotModal from '@/common/components/organisms/SlotModal.vue'
import { useSettingStore } from '@/stores/setting'
import { blankError } from '@/utils/errors'
import { stringToOption } from '@/utils/utils'

import { TableShape, TableShapeType } from '@generated/types'

export default defineComponent({
  components: {
    RadioInput,
    ButtonDefault,
    ErrorDisplay,
    NumberInput,
    SlotModal,
    TextInput,
    XClose,
  },
  props: {
    table: {
      type: Object,
      required: true,
    },
    isDefaultPrice: {
      type: Boolean,
      required: true,
    },
  },
  emits: {
    // eslint-disable-next-line no-unused-vars
    save: (_Table: any) => true,
    deleteTable: () => true,
    closeView: () => true,
  },

  data() {
    return {
      name: '',
      default_price_per_person: 0 as number,
      deleteTableDialog: false,
      width: 2,
      height: 2,
      table_type_id: '0',
      rotation: 0 as number,
      selectedTableShape: TableShape.SQUARE as TableShapeType,
      ui: {
        error: blankError(),
      },
      min_max_guests: [0, 9],
      isWidthAndHeightEqual: true,
      heldOption: '',
      TableShape,
    }
  },

  beforeMount() {
    this.init()
  },

  computed: {
    ...mapStores(useSettingStore),
    ...mapState(useSettingStore, ['table_types']),

    usesDiameter(): boolean {
      return (
        this.selectedTableShape == TableShape.CIRCLE ||
        this.selectedTableShape == TableShape.HALF_CIRCLE
      )
    },
    widthLabel(): string {
      if (this.usesDiameter) {
        return 'Diameter'
      } else {
        return 'Width'
      }
    },
    tableShapeChoices(): any[] {
      return Object.values(TableShape).map(stringToOption)
    },
    minimum_guests(): number {
      return Math.min(...this.min_max_guests)
    },
    maximum_guests(): number {
      return Math.max(...this.min_max_guests)
    },
    heldOptions(): string[] {
      // this is maybe overkill, but the idea is that these string constants are defined exactly once and could be i18n'ed
      return ['Available', 'Held']
    },
    held(): boolean {
      return this.heldOption == this.heldOptions[1]
    },
  },

  watch: {
    table() {
      this.init()
    },
    width(value) {
      if (this.isWidthAndHeightEqual) this.height = value
    },
    height(value) {
      if (this.isWidthAndHeightEqual) this.width = value
    },
    isWidthAndHeightEqual(value) {
      if (value) this.height = this.width
    },
  },

  methods: {
    init() {
      if (this.table) {
        this.table_type_id = (this.table.type_id || 0).toString()
        this.name = this.table.name
        const minimum_guests = this.table.minimum_guests || 0
        const maximum_guests = this.table.maximum_guests || 0
        this.min_max_guests = [minimum_guests, maximum_guests]
        this.height = this.table.h || 2
        this.width = this.table.w || 2
        this.rotation = this.table.rotation || 0
        this.selectedTableShape = this.table.shape || TableShape.SQUARE
        this.default_price_per_person =
          Number(this.table.price_per_person) || Number(this.table.default_price_per_person) || 0
        this.heldOption = this.heldOptions[this.table.held ? 1 : 0] // the 0th string is Available, the 1st is Held

        this.isWidthAndHeightEqual = this.width === this.height
      } else {
        this.heldOption = this.heldOptions[0]
      }
    },

    setSelectedTableShape(evt: string) {
      // it might be possible to do this in a more typesafe way, but this isn't so bad
      this.selectedTableShape = evt as TableShapeType
    },

    onSaveTable() {
      if (this.height < 0.5 || 50 < this.height) {
        this.ui.error.code = 'invalid_height'
        this.ui.error.message = 'Height must not be less than 0.5 or more than 50'
        return
      }
      if (this.width < 0.5 || 50 < this.width) {
        this.ui.error.code = 'invalid_width'
        this.ui.error.message = `${this.widthLabel} must not be less than 0.5 or more than 50`
        return
      }

      let table = {
        name: this.name,
        minimum_guests: this.minimum_guests,
        maximum_guests: this.maximum_guests,
        h: this.height,
        w: this.width,
        type_id: Number(this.table_type_id),
        shape: this.selectedTableShape,
        rotation: this.rotation,
        held: this.held,
        standing_room: this.table.standing_room,
      }

      if (this.isDefaultPrice) {
        table['default_price_per_person'] = Number(this.default_price_per_person)
      } else {
        table['price_per_person'] = Number(this.default_price_per_person)
      }

      this.$emit('save', table)
    },
  },
})
</script>
